import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  GraduationCap, Lock, CheckCircle2, XCircle, Circle, Loader2, ArrowLeft, Sparkles,
  ExternalLink, BookOpen, ArrowRight, Download, ClipboardList, Users, User, PlayCircle, ChevronDown,
  CalendarPlus, Package, Wand2,
} from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { MyTraining, Quiz, QuizResult, TrainingModule, AaiPolicy, AaiVideo } from '../../types/aai';
import { Loading, ErrorNote, CertBadge } from './components';
import { Card, MonoLabel } from './dashboards/ui';

type View = 'list' | 'quiz' | 'result' | 'reading' | 'homework';

// A step's attached video, embedded (YouTube/Vimeo → iframe; else a <video> tag).
function embedSrc(v: AaiVideo): string | null {
  if (v.provider === 'youtube') { const m = v.url.match(/(?:youtu\.be\/|[?&]v=)([\w-]{11})/); return m ? `https://www.youtube.com/embed/${m[1]}` : null; }
  if (v.provider === 'vimeo') { const m = v.url.match(/vimeo\.com\/(\d+)/); return m ? `https://player.vimeo.com/video/${m[1]}` : null; }
  return null;
}
const VideoPlayer: React.FC<{ video: AaiVideo }> = ({ video }) => {
  const embed = embedSrc(video);
  return (
    <div className="mb-3">
      {embed ? (
        <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
          <iframe src={embed} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen className="absolute inset-0 h-full w-full" />
        </div>
      ) : (
        <video src={video.url} controls className="w-full rounded-lg bg-black" />
      )}
    </div>
  );
};

// Tiny "Group · Story-based" session brief on a row (Trainer Framework §3).
const METHOD_LABEL: Record<string, string> = { explainer: 'Explainer', story: 'Story-based', analogy: 'Analogy' };
const ModuleBrief: React.FC<{ m: TrainingModule }> = ({ m }) => {
  if (!m.method && !m.delivery_mode) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
      {m.delivery_mode === 'group' ? <Users size={11} /> : m.delivery_mode === 'solo' ? <User size={11} /> : null}
      {m.delivery_mode && <span className="capitalize">{m.delivery_mode === 'solo' ? '1:1' : m.delivery_mode}</span>}
      {m.method && <span>· {METHOD_LABEL[m.method] || m.method}</span>}
    </span>
  );
};

// Group modules into their phases in payload order (Module 1 → …).
type PhaseGroup = { phase: string; week: string | null; in_tier: boolean; modules: TrainingModule[] };
function byPhase(modules: TrainingModule[]): PhaseGroup[] {
  const groups: PhaseGroup[] = [];
  for (const m of modules) {
    const phase = m.phase || 'Training';
    const last = groups[groups.length - 1];
    if (last && last.phase === phase) { last.modules.push(m); continue; }
    const week = m.week_start != null && m.week_end != null
      ? (m.week_start === m.week_end ? `Week ${m.week_start}` : `Weeks ${m.week_start}–${m.week_end}`)
      : null;
    groups.push({ phase, week, in_tier: m.in_tier !== false, modules: [m] });
  }
  return groups;
}

const TIER_LABEL: Record<string, string> = { '3mo': '3-month', '6mo': '6-month', '12mo': '12-month' };

const MyTrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const [training, setTraining] = useState<MyTraining | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<View>('list');
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [reading, setReading] = useState<TrainingModule | null>(null);
  const [homework, setHomework] = useState<TrainingModule | null>(null);
  const [submitText, setSubmitText] = useState('');
  const [policy, setPolicy] = useState<AaiPolicy | null>(null);
  const [exporting, setExporting] = useState(false);
  // Collapsible phases — keyed by phase name, default expanded (absent = open).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const togglePhase = (phase: string) => setCollapsed((c) => ({ ...c, [phase]: !c[phase] }));

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await aaiApi.getMyTraining();
      setTraining(data);
      setError(null);
    } catch {
      setError('Could not load your training.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openQuiz = async (m: TrainingModule) => {
    setBusy(true); setError(null);
    try {
      const { data } = await aaiApi.getQuiz(m.id);
      setQuiz(data); setAnswers({}); setResult(null); setView('quiz');
    } catch {
      setError('Could not open this quiz.');
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!quiz) return;
    setBusy(true);
    try {
      const ordered = quiz.questions.map((_, i) => (answers[i] ?? -1));
      const { data } = await aaiApi.submitQuiz(quiz.id, ordered);
      setResult(data); setView('result');
    } catch {
      setError('Could not submit your answers.');
    } finally { setBusy(false); }
  };

  const backToList = () => { setView('list'); setQuiz(null); setResult(null); setReading(null); setHomework(null); setSubmitText(''); loadList(); };

  // Reading / micro-learning module: show the sections; the acceptable-use step
  // also loads the org policy.
  const openReading = async (m: TrainingModule) => {
    setReading(m); setPolicy(null); setView('reading');
    if (m.feature?.key === 'whats_allowed') {
      try { const { data } = await aaiApi.getPolicy(); setPolicy(data); } catch { /* policy optional */ }
    }
  };

  // Homework: show instructions + a submission box (text / link / just mark done).
  const openHomework = (m: TrainingModule) => { setHomework(m); setSubmitText(''); setView('homework'); };
  const submitHomework = async () => {
    if (!homework) return;
    setBusy(true);
    try {
      const st = homework.submit_type || 'none';
      const payload = st === 'none' ? undefined : st === 'link' ? { url: submitText.trim() } : { text: submitText.trim() };
      await aaiApi.completeStep(homework.id, payload);
      backToList();
    } catch { setError('Could not submit your homework.'); }
    finally { setBusy(false); }
  };

  // Link module: jump to the anchored feature. "Visit" links complete on open;
  // "activity" links (build/vet/share/dives) complete only when the real work is
  // done (a server-side derived rule), so we must NOT mark those done on click.
  const openLink = async (m: TrainingModule) => {
    if (!m.auto_complete) {
      setBusy(true);
      try { await aaiApi.completeStep(m.id); } catch { /* non-fatal */ }
      finally { setBusy(false); }
    }
    if (!m.feature?.route) return;
    // SKL-8: a lesson with a starter skill lands in the builder pre-seeded from
    // that real skill — "customise this", never a blank box.
    if (m.starter_skill && m.feature.route.startsWith('/skill-builder')) {
      navigate(`${m.feature.route}?from=${m.starter_skill.id}`);
      return;
    }
    navigate(m.feature.route);
  };

  // DEL-2 (v1): hold a 20-minute block on the participant's calendar for this
  // lesson — a plain .ics that works against every calendar, no OAuth grant.
  const holdCalendarBlock = async (m: TrainingModule) => {
    try {
      const res = await aaiApi.downloadLessonIcs(m.id);
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'lesson.ics';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setError('Could not build the calendar block.'); }
  };

  // DEL-2: a calendar-event link (?lesson=<id>) lands straight in that lesson —
  // same per-kind handler as clicking the row's CTA. Fires once per page load,
  // then clears the param so back-navigation doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || !training?.readiness_complete) return;
    const target = searchParams.get('lesson');
    if (!target) return;
    deepLinkHandled.current = true;
    setSearchParams({}, { replace: true });
    const m = training.modules.find((x) => x.id === target);
    if (!m) return;
    if (m.kind === 'quiz') { openQuiz(m); return; }
    if (m.kind === 'homework') { openHomework(m); return; }
    if (m.kind === 'link') { openLink(m); return; }
    openReading(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [training]);

  const markDone = async (m: TrainingModule) => {
    setBusy(true);
    try { await aaiApi.completeStep(m.id); backToList(); }
    catch { setError('Could not mark this done.'); }
    finally { setBusy(false); }
  };

  const exportBaseline = async () => {
    setExporting(true);
    try {
      const res = await aaiApi.downloadBaselinePdf();
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'my-ai-baseline.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setError('Could not export your baseline yet — complete your readiness check first.'); }
    finally { setExporting(false); }
  };

  if (loading) return <Loading label="Loading your training…" />;
  if (error && view === 'list') return <ErrorNote message={error} />;

  // ── Locked (readiness not yet complete) ──────────────────────────────────
  if (training && !training.readiness_complete) {
    return (
      <div className="max-w-2xl mx-auto">
        <Header />
        <Card className="text-center py-10">
          <span className="mx-auto mb-4 grid place-items-center h-14 w-14 rounded-full bg-gray-100 text-gray-400">
            <Lock size={24} />
          </span>
          <p className="text-[15px] font-semibold text-gray-900 mb-1">Your training unlocks after your readiness check</p>
          <p className="text-[13px] text-gray-500 max-w-sm mx-auto mb-5">
            Complete the quick readiness conversation with Orbit and we'll line up the right lessons for you.
          </p>
          <button onClick={() => navigate('/onboarding')}
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[14px] font-semibold px-5 py-2.5 whitespace-nowrap">
            <Sparkles size={16} /> Complete my readiness check
          </button>
        </Card>
      </div>
    );
  }

  // ── Quiz taking ──────────────────────────────────────────────────────────
  if (view === 'quiz' && quiz) {
    const allAnswered = quiz.questions.every((_, i) => answers[i] !== undefined);
    return (
      <div className="max-w-2xl mx-auto">
        <button onClick={backToList} className="inline-flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft size={15} /> Back to training
        </button>
        <div className="flex items-center gap-2 mb-1"><GraduationCap size={22} className="text-brand-500" /><h1 className="text-2xl font-semibold text-gray-900">{quiz.title}</h1></div>
        <p className="text-sm text-gray-500 mb-6">Pick the best answer for each question. You need {Math.round(quiz.pass_score * 100)}% to pass.</p>

        <div className="space-y-4">
          {quiz.questions.map((q, qi) => (
            <Card key={qi}>
              <p className="text-[14px] font-semibold text-gray-900 mb-3">{qi + 1}. {q.q}</p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[qi] === oi;
                  return (
                    <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                      className={`w-full text-left flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-[13.5px] transition-colors
                        ${selected ? 'border-brand-400 bg-brand-50 text-gray-900' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`}>
                      <span className={`h-4 w-4 rounded-full border grid place-items-center ${selected ? 'border-brand-500' : 'border-gray-300'}`}>
                        {selected && <span className="h-2 w-2 rounded-full bg-brand-500" />}
                      </span>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>

        {error && <div className="mt-3"><ErrorNote message={error} /></div>}
        <div className="flex justify-end mt-5">
          <button onClick={submit} disabled={busy || !allAnswered}
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[14px] font-semibold px-5 py-2.5 whitespace-nowrap">
            {busy && <Loader2 size={16} className="animate-spin" />} Submit answers
          </button>
        </div>
      </div>
    );
  }

  // ── Result ───────────────────────────────────────────────────────────────
  if (view === 'result' && result && quiz) {
    return (
      <div className="max-w-2xl mx-auto">
        <Header />
        <Card className="text-center py-8 mb-4">
          <span className={`mx-auto mb-3 grid place-items-center h-14 w-14 rounded-full ${result.passed ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
            {result.passed ? <CheckCircle2 size={26} /> : <XCircle size={26} />}
          </span>
          <p className="text-[17px] font-semibold text-gray-900">{result.passed ? 'Passed!' : 'Not quite yet'}</p>
          <p className="text-[13px] text-gray-500 mt-1">You got {result.correct} of {result.total} correct ({Math.round(result.score * 100)}%).</p>
          {result.attempts > 1 && (
            <p className="text-[12px] text-gray-400 mt-0.5">Attempt {result.attempts} · best {Math.round(result.best_score * 100)}%</p>
          )}
          <div className="flex items-center justify-center gap-3 mt-5">
            {!result.passed && (
              <button onClick={() => openQuiz({ id: quiz.id } as TrainingModule)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
                Try again
              </button>
            )}
            <button onClick={backToList} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
              Back to training
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Reading (link/reading step detail) ─────────────────────────────────────
  if (view === 'reading' && reading) {
    return (
      <div className="max-w-2xl mx-auto">
        <button onClick={backToList} className="inline-flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft size={15} /> Back to training
        </button>
        <div className="flex items-center gap-2 mb-1"><BookOpen size={22} className="text-brand-500" /><h1 className="text-2xl font-semibold text-gray-900">{reading.title}</h1></div>
        {reading.description && <p className="text-sm text-gray-500 mb-5">{reading.description}</p>}
        {reading.video && <VideoPlayer video={reading.video} />}
        <div className="space-y-3">
          {(reading.body ?? []).map((s, i) => (
            <Card key={i}>
              <p className="text-[14px] font-semibold text-gray-900 mb-1">{s.h}</p>
              <p className="text-[13.5px] text-gray-600 leading-relaxed">{s.p}</p>
            </Card>
          ))}
          {reading.feature?.key === 'whats_allowed' && (
            <Card>
              <MonoLabel className="mb-2">Your organization's AI policy</MonoLabel>
              {policy && policy.body.trim()
                ? <p className="text-[13.5px] text-gray-700 whitespace-pre-wrap leading-relaxed">{policy.body}</p>
                : <p className="text-[13px] text-gray-400">Your organization hasn't published its AI policy yet — check What's Allowed for approved tools and use-cases.</p>}
              {reading.feature?.route && (
                <button onClick={() => navigate(reading.feature!.route)}
                  className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand-600 hover:text-brand-700">
                  Open What's Allowed <ArrowRight size={14} />
                </button>
              )}
            </Card>
          )}
        </div>
        {error && <div className="mt-3"><ErrorNote message={error} /></div>}
        <div className="flex justify-end mt-5">
          <button onClick={() => markDone(reading)} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[14px] font-semibold px-5 py-2.5 whitespace-nowrap">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {reading.status === 'completed' ? 'Done' : 'Mark as done'}
          </button>
        </div>
      </div>
    );
  }

  // ── Homework (instructions + submission) ───────────────────────────────────
  if (view === 'homework' && homework) {
    const st = homework.submit_type || 'none';
    const done = homework.status === 'completed';
    const canSubmit = st === 'none' || submitText.trim().length > 0;
    return (
      <div className="max-w-2xl mx-auto">
        <button onClick={backToList} className="inline-flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft size={15} /> Back to training
        </button>
        <div className="flex items-center gap-2 mb-1"><ClipboardList size={22} className="text-brand-500" /><h1 className="text-2xl font-semibold text-gray-900">{homework.title}</h1></div>
        {homework.description && <p className="text-sm text-gray-500 mb-5">{homework.description}</p>}
        {homework.video && <VideoPlayer video={homework.video} />}
        <Card className="mb-4">
          <MonoLabel className="mb-2">Your task</MonoLabel>
          <p className="text-[13.5px] text-gray-700 whitespace-pre-wrap leading-relaxed">{homework.instructions || 'Complete the task described by your trainer.'}</p>
        </Card>
        {st !== 'none' && (
          <Card className="mb-4">
            <MonoLabel className="mb-2">{st === 'link' ? 'Share a link' : 'Your answer'}</MonoLabel>
            {st === 'link' ? (
              <input value={submitText} onChange={(e) => setSubmitText(e.target.value)} placeholder="https://…"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
            ) : (
              <textarea value={submitText} onChange={(e) => setSubmitText(e.target.value)} rows={4} placeholder="Write your response…"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
            )}
          </Card>
        )}
        {error && <div className="mt-3"><ErrorNote message={error} /></div>}
        <div className="flex justify-end mt-1">
          <button onClick={submitHomework} disabled={busy || !canSubmit}
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[14px] font-semibold px-5 py-2.5 whitespace-nowrap">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {done ? 'Resubmit' : st === 'none' ? 'Mark as done' : 'Submit'}
          </button>
        </div>
      </div>
    );
  }

  // ── List (phase-grouped, kind-aware) ────────────────────────────────────────
  const modules = training?.modules ?? [];
  // Micro-learning steps are the design's "Learn between sessions" lessons —
  // pulled out into their own card so the phase list stays the core modules.
  const microLessons = modules.filter((m) => m.kind === 'micro_learning' && m.in_tier !== false);
  const coreModules = modules.filter((m) => m.kind !== 'micro_learning');
  const groups = byPhase(coreModules);
  return (
    <div className="max-w-2xl mx-auto">
      <Header stage={training?.stage ?? null} tier={training?.tier ?? null} />
      {training && (
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="text-[12.5px] text-gray-500">
            {training.summary.completed} of {training.summary.total} completed
          </p>
          <button onClick={exportBaseline} disabled={exporting}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 hover:text-brand-700 disabled:opacity-50">
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export my baseline
          </button>
        </div>
      )}
      {error && <div className="mb-3"><ErrorNote message={error} /></div>}
      {modules.length === 0 ? (
        <Card><p className="text-[13px] text-gray-400 text-center py-6">No training assigned yet — check back soon.</p></Card>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => g.in_tier ? (
            <div key={g.phase}>
              <PhaseHeader group={g} open={!collapsed[g.phase]} onToggle={() => togglePhase(g.phase)} />
              {!collapsed[g.phase] && (
                <div className="space-y-2">
                  {g.modules.map((m) => (
                    <ModuleRow key={m.id} m={m} busy={busy}
                      onQuiz={() => openQuiz(m)} onReading={() => openReading(m)} onLink={() => openLink(m)} onHomework={() => openHomework(m)}
                      onHoldBlock={() => holdCalendarBlock(m)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Out-of-tier phase — collapsed and NOT expandable while locked: only
            // the header shows (a Lock sits where the chevron would be on unlocked
            // phases), with the count of what's inside and an upsell to unlock it.
            <div key={g.phase} className="opacity-70">
              <div className="w-full flex items-center gap-3 mb-2 px-1 py-0.5">
                <Lock size={15} className="text-gray-300 shrink-0" />
                <div className="min-w-0 shrink-0 max-w-[42%]">
                  <p className="text-[13px] font-semibold text-gray-500 truncate">{g.phase}</p>
                  {g.week && <span className="text-[11px] font-sans uppercase tracking-wide text-gray-400">{g.week}</span>}
                </div>
                <div className="flex-1" />
                <span className="shrink-0 text-[11px] font-medium text-gray-400 tabular-nums">{g.modules.length} lesson{g.modules.length === 1 ? '' : 's'}</span>
                <span className="shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap bg-brand-50 text-brand-700">6-month program</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {microLessons.length > 0 && (
        <div className="mt-5 bg-card rounded-aa p-5">
          <MonoLabel className="mb-1">Learn between sessions</MonoLabel>
          <p className="text-[11.5px] text-gray-500 mb-3">Short, on-demand lessons — momentum doesn't wait for the next workshop.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {microLessons.map((m) => (
              <button key={m.id} onClick={() => openReading(m)} className="aa-lift text-left bg-white rounded-[11px] p-4">
                <PlayCircle size={18} className="text-brand-500" />
                <div className="text-[13px] font-semibold text-gray-900 mt-2 mb-0.5">{m.title}</div>
                <div className="text-[11.5px] text-gray-500">{m.status === 'completed' ? 'Watched' : m.video ? 'Video lesson' : 'Short read'}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Phase group header — the design's per-module progress bar + status pill.
// Progress and state are derived from real step completion (never gates next).
const PHASE_STATE = {
  complete: { label: 'Complete', cls: 'bg-emerald-50 text-emerald-700' },
  progress: { label: 'In progress', cls: 'bg-brand-50 text-brand-700' },
  notstarted: { label: 'Not started', cls: 'bg-gray-100 text-gray-500' },
} as const;
const PhaseHeader: React.FC<{ group: PhaseGroup; open: boolean; onToggle: () => void }> = ({ group, open, onToggle }) => {
  const total = group.modules.length;
  const done = group.modules.filter((m) => m.status === 'completed').length;
  const pctNum = total ? Math.round((done / total) * 100) : 0;
  const state = total > 0 && done === total ? PHASE_STATE.complete : done > 0 ? PHASE_STATE.progress : PHASE_STATE.notstarted;
  return (
    <button onClick={onToggle} aria-expanded={open}
      className="w-full flex items-center gap-3 mb-2 text-left rounded-lg -mx-1 px-1 py-0.5 hover:bg-gray-50">
      <ChevronDown size={15} className={`text-gray-400 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
      <div className="min-w-0 shrink-0 max-w-[42%]">
        <p className="text-[13px] font-semibold text-gray-900 truncate">{group.phase}</p>
        {group.week && <span className="text-[11px] font-sans uppercase tracking-wide text-gray-400">{group.week}</span>}
      </div>
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 min-w-[48px]">
        <div className="h-1.5 rounded-full bg-brand-500 transition-all" style={{ width: `${pctNum}%` }} />
      </div>
      <span className="shrink-0 text-[11px] font-medium text-gray-400 tabular-nums">{done}/{total}</span>
      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${state.cls}`}>{state.label}</span>
    </button>
  );
};

const Header: React.FC<{ stage?: string | null; tier?: string | null }> = ({ stage, tier }) => (
  <>
    <div className="flex items-center gap-2 mb-1 flex-wrap">
      <GraduationCap size={22} className="text-brand-500" />
      <h1 className="text-2xl font-semibold text-gray-900">My Training</h1>
      {stage && (
        <span className="inline-flex items-center rounded-full bg-brand-50 text-brand-700 px-2.5 py-0.5 text-[11px] font-semibold capitalize">
          {stage}
        </span>
      )}
      {tier && TIER_LABEL[tier] && (
        <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 px-2.5 py-0.5 text-[11px] font-semibold">
          {TIER_LABEL[tier]} program
        </span>
      )}
      <CertBadge />
    </div>
    <p className="text-sm text-gray-500 mb-4">Your guided path — hands-on lessons, short reads, and knowledge checks.</p>
  </>
);

// One module row, rendered by kind: quiz (take/retake), link (open the anchored
// feature), reading / micro-learning (open the explainer), homework (open the
// task). Completion never gates the next step.
// DEL-1: the completion stamp — "Completed Tue 14:20 · self-guided".
function completionStamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const day = d.toLocaleDateString(undefined, { weekday: 'short' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `Completed ${day} ${time} · self-guided`;
}

const ModuleRow: React.FC<{
  m: TrainingModule; busy: boolean;
  onQuiz: () => void; onReading: () => void; onLink: () => void; onHomework: () => void;
  onHoldBlock: () => void;
}> = ({ m, busy, onQuiz, onReading, onLink, onHomework, onHoldBlock }) => {
  const done = m.status === 'completed';
  // Per-kind row presentation — one table instead of four parallel switches.
  const quizMeta = `${m.question_count} question${m.question_count === 1 ? '' : 's'}${done && m.score != null ? ` · scored ${Math.round(m.score * 100)}%` : ''}`;
  const linkMeta = m.feature ? `Opens ${m.feature.label}${m.feature.live ? '' : ' · coming soon'}` : 'Hands-on';
  const UI: Record<string, { meta: string; cta: string; icon: React.ElementType | null }> = {
    quiz: { meta: quizMeta, cta: done ? 'Retake' : 'Start', icon: null },
    link: { meta: linkMeta, cta: 'Open', icon: ExternalLink },
    homework: { meta: 'Homework', cta: done ? 'Review' : 'Start', icon: ClipboardList },
    micro_learning: { meta: 'Micro-learning', cta: done ? 'Review' : 'Read', icon: BookOpen },
    reading: { meta: 'Short read', cta: done ? 'Review' : 'Read', icon: BookOpen },
  };
  const { meta, cta, icon: Icon } = UI[m.kind] ?? UI.reading;
  // reading + micro-learning share the reading view; the rest have their own.
  const HANDLERS: Record<string, () => void> = { quiz: onQuiz, link: onLink, homework: onHomework };
  const onClick = HANDLERS[m.kind] ?? onReading;
  const stamp = done ? completionStamp(m.completed_at) : null;
  const topTools = (m.unlocks?.per_tool ?? []).slice(0, 3);
  return (
    <Card className="flex items-center gap-3.5">
      <StatusIcon status={m.status} passed={m.passed} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[14px] text-gray-900 truncate">{m.title}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] text-gray-500 truncate">{meta}{m.video ? ' · video' : ''}</span>
          <ModuleBrief m={m} />
          {stamp && <span className="text-[11px] text-emerald-600">{stamp}</span>}
        </div>
        {m.artifact && (
          <p className="text-[11.5px] text-gray-400 mt-0.5 flex items-center gap-1 truncate">
            <Package size={11} className="shrink-0" />
            <span className="truncate">{done ? 'You now own' : "You'll own"}: {m.artifact}</span>
          </p>
        )}
        {m.starter_skill && (
          <p className="text-[11.5px] text-brand-600 mt-0.5 flex items-center gap-1 truncate">
            <Wand2 size={11} className="shrink-0" />
            <span className="truncate">Starts from a real skill: {m.starter_skill.name}</span>
          </p>
        )}
        {topTools.length > 0 && !done && (
          <p className="text-[11.5px] text-gray-400 mt-0.5 truncate">
            Unlocks ready-made skills: {topTools.map((t) => `${t.tool} (${t.skills})`).join(' · ')}
          </p>
        )}
      </div>
      {!done && (
        <button onClick={onHoldBlock} disabled={busy} title="Hold 20 minutes on my calendar"
          className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full border border-gray-200 text-gray-400 hover:text-brand-600 hover:border-brand-300 disabled:opacity-50">
          <CalendarPlus size={14} />
        </button>
      )}
      <button onClick={onClick} disabled={busy}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12.5px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
        {Icon && <Icon size={13} />} {cta}
      </button>
    </Card>
  );
};

const StatusIcon: React.FC<{ status: string; passed: boolean | null }> = ({ status, passed }) => {
  if (status !== 'completed') return <Circle size={20} className="text-gray-300 shrink-0" />;
  return passed
    ? <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
    : <XCircle size={20} className="text-amber-500 shrink-0" />;
};

export default MyTrainingPage;
